import { useCallback, useEffect, useState } from 'react'
import { Box, Button, Title } from '@holbonkit/ui'
import { clsx } from 'clsx'

import { useCreateSberBeneficiary } from '@/shared/api'
import { useAppNavbar } from '@/shared/hooks/use-app-navbar'
import { ProgressBar, SmzFormLoading } from '@/shared/ui'

import { buildSberBeneficiaryPayload } from '../lib/build-sber-beneficiary-payload'
import type { TCitizenship, TSmzFormData } from '../model/types'
import { Address, ADDRESS_FORM_ID, type TAddressForm } from '../smz-form/address'
import { Announce } from '../smz-form/announce'
import {
    Documents,
    DOCUMENTS_FORM_ID,
    type TDocumentsFormNotRF,
    type TDocumentsFormRF,
} from '../smz-form/documents'
import {
    PERSONAL_DATA_FORM_ID,
    PersonalData,
    type TPersonalDataForm,
} from '../smz-form/personal-data'
import { Processing } from '../smz-form/processing'
import { Sbp, SBP_FORM_ID } from '../smz-form/sbp'
import type { TSbpForm } from '../smz-form/sbp/types'

import styles from './smz-form.module.scss'

import { IllustrationPassport } from '@/assets'

const FORM_ID_BY_STEP: Record<number, string> = {
    1: PERSONAL_DATA_FORM_ID,
    2: DOCUMENTS_FORM_ID,
    3: ADDRESS_FORM_ID,
    4: SBP_FORM_ID,
}

export const SmzForm = () => {
    useAppNavbar(' ', 'Назад', 'onNavbarBackButtonClick()')
    const [citizenship, setCitizenship] = useState<TCitizenship>('unknown')
    const isLoading = false
    const [currentStep, setCurrentStep] = useState<number>(0)
    const [formData, setFormData] = useState<Partial<TSmzFormData>>({})
    const [isStepValid, setIsStepValid] = useState(false)
    const totalSteps = 4

    const handleNavbarBackButtonClick = useCallback(() => {
        setCurrentStep((previous) => {
            if (previous > 0) {
                return previous - 1
            }

            return previous
        })
    }, [])

    useEffect(() => {
        globalThis.window.onNavbarBackButtonClick = handleNavbarBackButtonClick

        return () => {
            delete globalThis.window.onNavbarBackButtonClick
        }
    }, [handleNavbarBackButtonClick])

    const { mutateAsync: mutateAsyncSberBeneficiary } = useCreateSberBeneficiary()

    const handleSbpSubmit = async (sbp: TSbpForm) => {
        const { personalData, documents, address } = formData

        if (!personalData || !documents || !address) {
            return
        }

        try {
            if (citizenship === 'rf' || citizenship === 'not_rf') {
                await mutateAsyncSberBeneficiary({
                    data: buildSberBeneficiaryPayload(citizenship, {
                        personalData,
                        documents,
                        address,
                        sbp,
                    }),
                })
            }
            setFormData((previous) => ({ ...previous, sbp }))
            setCurrentStep(5)
        } catch (error) {
            console.error(error)
        }
    }

    if (isLoading) {
        return <SmzFormLoading />
    }

    if (citizenship === 'unknown') {
        return (
            <Box className={styles.isRF} pb={4} pl={4} pr={4}>
                <Box className={styles['isRF__illustrationContent']} mt={4} pt={10}>
                    <img
                        alt='RF'
                        className={styles['isRF__illustrationContent--image']}
                        src={IllustrationPassport}
                    />
                    <Title variant='header-24-bold'>
                        У вас есть
                        <br />
                        гражданство РФ?
                    </Title>
                </Box>
                <Box className={styles['isRF__buttons']}>
                    <Button size='lg' onClick={() => setCitizenship('rf')}>
                        Да
                    </Button>
                    <Button size='lg' variant='secondary' onClick={() => setCitizenship('not_rf')}>
                        Нет
                    </Button>
                </Box>
            </Box>
        )
    }

    return (
        <Box className={styles['smz-form']}>
            {currentStep === 0 && <Announce onNext={() => setCurrentStep(currentStep + 1)} />}
            {currentStep === 1 && (
                <PersonalData
                    key={currentStep}
                    defaultValues={formData.personalData}
                    formId={PERSONAL_DATA_FORM_ID}
                    onValidityChange={setIsStepValid}
                    onSubmit={(personalData: TPersonalDataForm) => {
                        setFormData((previous) => ({ ...previous, personalData }))
                        setCurrentStep(2)
                    }}
                />
            )}
            {currentStep === 2 && (
                <Documents
                    key={currentStep}
                    citizenship={citizenship}
                    defaultValues={formData.documents}
                    formId={DOCUMENTS_FORM_ID}
                    onValidityChange={setIsStepValid}
                    onSubmit={(documents: TDocumentsFormNotRF | TDocumentsFormRF) => {
                        setFormData((previous) => ({ ...previous, documents }))
                        setCurrentStep(3)
                    }}
                />
            )}
            {currentStep === 3 && (
                <Address
                    key={currentStep}
                    citizenship={citizenship}
                    defaultValues={formData.address}
                    formId={ADDRESS_FORM_ID}
                    onValidityChange={setIsStepValid}
                    onSubmit={(address: TAddressForm) => {
                        setFormData((previous) => ({ ...previous, address }))
                        setCurrentStep(4)
                    }}
                />
            )}
            {currentStep === 4 && (
                <Sbp
                    key={currentStep}
                    defaultValues={formData.sbp}
                    formId={SBP_FORM_ID}
                    onSubmit={handleSbpSubmit}
                    onValidityChange={setIsStepValid}
                />
            )}
            {currentStep === 5 && <Processing />}
            {currentStep !== 0 && currentStep !== 5 && (
                <Box className={styles['progress-bar-container']}>
                    <ProgressBar currentStep={currentStep} totalSteps={totalSteps} />
                    <Button
                        aria-disabled={!isStepValid}
                        form={FORM_ID_BY_STEP[currentStep]}
                        size='lg'
                        type='submit'
                        className={clsx(
                            styles['progress-bar-container__button'],
                            !isStepValid && styles['progress-bar-container__button--disabled'],
                        )}
                    >
                        Далее
                    </Button>
                </Box>
            )}
        </Box>
    )
}
